<div class="calc-shell">
  <div class="calc">
    <h2 i18n="@@standort.calc.h2">Standort-Score-Rechner</h2>
    <p class="intro" i18n="@@standort.calc.intro">
      Beschreiben Sie Ihren geplanten Standort in Kategorien – Sie müssen keine Vorbeifahrten zählen.
      In unter einer Minute bekommen Sie einen Score, einen Umsatzkorridor und Ihre zwei größten Hebel.
    </p>

    <!-- Modus -->
    <div class="field">
      <label i18n="@@standort.calc.modus.label">Steht der Automat vor einem Ihrer Geschäfte?
        <span class="hint">– das entscheidet die Rechenweise</span></label>
      <div class="seg two">
        <button type="button" [attr.aria-pressed]="input().modus === 'eigenstaendig'"
                (click)="select('modus', 'eigenstaendig')" i18n="@@standort.calc.modus.nein">Nein, eigenständiger Standort</button>
        <button type="button" [attr.aria-pressed]="input().modus === 'filiale'"
                (click)="select('modus', 'filiale')" i18n="@@standort.calc.modus.ja">Ja, vor einem meiner Geschäfte</button>
      </div>
    </div>

    <div class="field">
      <label i18n="@@standort.calc.art.label">Art des Standorts</label>
      <div class="seg">
        <button *ngFor="let o of artOptions" type="button"
                [attr.aria-pressed]="input().art === o.val" (click)="select('art', o.val)">{{ o.label }}</button>
      </div>
    </div>

    <div class="field">
      <label i18n="@@standort.calc.strasse.label">Straße direkt davor</label>
      <div class="seg">
        <button *ngFor="let o of strasseOptions" type="button"
                [attr.aria-pressed]="input().strasse === o.val" (click)="select('strasse', o.val)">{{ o.label }}</button>
      </div>
    </div>

    <div class="field">
      <label i18n="@@standort.calc.sicht.label">Sichtbarkeit</label>
      <div class="seg">
        <button *ngFor="let o of sichtOptions" type="button"
                [attr.aria-pressed]="input().sicht === o.val" (click)="select('sicht', o.val)">{{ o.label }}</button>
      </div>
    </div>

    <div class="field">
      <label i18n="@@standort.calc.parken.label">Parken</label>
      <div class="seg">
        <button *ngFor="let o of parkenOptions" type="button"
                [attr.aria-pressed]="input().parken === o.val" (click)="select('parken', o.val)">{{ o.label }}</button>
      </div>
    </div>

    <!-- LEH nur im Modus 'eigenstaendig' -->
    <div class="field" *ngIf="input().modus === 'eigenstaendig'">
      <label i18n="@@standort.calc.leh.label">Nächster Lebensmittelhandel <span class="hint">– der dominante Faktor</span></label>
      <div class="seg">
        <button *ngFor="let o of lehOptions" type="button"
                [attr.aria-pressed]="input().leh === o.val" (click)="select('leh', o.val)">{{ o.label }}</button>
      </div>
    </div>

    <div class="field">
      <label i18n="@@standort.calc.haushalte.label">Haushalte im Umkreis von 2 km:
        <span class="rangeval">{{ input().haushalte | number }}</span></label>
      <input type="range" min="100" max="4000" step="100"
             [value]="input().haushalte" (input)="setHaushalte($any($event.target).value)" />
    </div>

    <!-- Zusatzfrage nur im Modus 'filiale' -->
    <div class="field" *ngIf="input().modus === 'filiale'">
      <label i18n="@@standort.calc.sonntag.label">Haben Sie sonntags geöffnet?
        <span class="hint">– erklärt einen Großteil des Zusatzumsatzes</span></label>
      <div class="seg two">
        <button type="button" [attr.aria-pressed]="!input().sonntagGeoeffnet"
                (click)="select('sonntagGeoeffnet', false)" i18n="@@standort.calc.sonntag.nein">Nein, sonntags geschlossen</button>
        <button type="button" [attr.aria-pressed]="input().sonntagGeoeffnet"
                (click)="select('sonntagGeoeffnet', true)" i18n="@@standort.calc.sonntag.ja">Ja, sonntags geöffnet</button>
      </div>
    </div>

    <button class="btn btn--dark block evalbtn" type="button" (click)="auswerten()" i18n="@@standort.calc.auswerten">Standort auswerten</button>

    <!-- Ergebnis -->
    <div class="result" *ngIf="evaluated()">
      <div class="score-head">
        <div class="gauge" [style.--p]="result().score" [style.--c]="gaugeColor()">
          <span>{{ result().score }}<small>/100</small></span>
        </div>
        <div>
          <p class="stufe">{{ result().stufe.label }}</p>
          <p class="sub">{{ result().stufe.sub }}</p>
        </div>
      </div>

      <div class="metrics">
        <div class="metric">
          <div class="k" i18n="@@standort.result.umsatz">Erwarteter Umsatz</div>
          <div class="v" i18n="@@standort.result.umsatz.value">{{ euro(result().revenue.low) }} – {{ euro(result().revenue.high) }}
            <small>/ Monat</small></div>
        </div>
        <div class="metric">
          <div class="k" i18n="@@standort.result.breakeven">Break-even beim Kauf</div>
          <div class="v">{{ amortLabel() }} <small>{{ amortSub() }}</small></div>
        </div>
      </div>

      <div class="hebel">
        <h4 i18n="@@standort.result.hebel.h4">Ihre zwei größten Hebel</h4>
        <ol>
          <li *ngFor="let h of result().hebel"><strong>{{ h.title }}</strong> {{ h.text }}</li>
        </ol>
      </div>

      <div class="compare" *ngIf="result().vergleich as v">
        <h4 i18n="@@standort.compare.h4">Vergleich: derselbe Aufwand an einem freien Standort in Ihrer Region</h4>
        <div class="compare-grid">
          <div class="compare-col">
            <div class="lab" i18n="@@standort.compare.filiale">Vor Ihrer Filiale</div>
            <div class="big">{{ v.filiale.score }} / 100</div>
            <div class="rev" i18n="@@standort.compare.filiale.rev">{{ euro(result().revenue.low) }} – {{ euro(result().revenue.high) }} /Mon.</div>
          </div>
          <div class="compare-col hl">
            <div class="lab" i18n="@@standort.compare.frei">Freier Standort ohne Supermarkt</div>
            <div class="big">{{ v.frei.score }} / 100</div>
            <div class="rev" i18n="@@standort.compare.frei.rev">rund das {{ faktorLabel() }}-fache</div>
          </div>
        </div>
        <p class="small" i18n="@@standort.compare.small">
          Gerechnet mit denselben Haushalten in Ihrer Umgebung, aber an einem freien Standort mit
          Durchgangsverkehr und ohne Supermarkt im Umkreis. Viele Betreiber starten vor der Filiale
          und stellen die zweite Box genau so auf.
        </p>
      </div>

      <div class="result-cta">
        <a class="btn" [routerLink]="amortLink" [queryParams]="amortQueryParams()" i18n="@@standort.cta.amort">Break-even genau berechnen →</a>
        <a class="btn btn--dark" href="#kontakt" (click)="scrollToAnchor($event, 'kontakt')" i18n="@@standort.cta.kontakt">Standort mit uns besprechen</a>
      </div>

      <p class="disclaimer" i18n="@@standort.disclaimer.marge">
        <strong>{{ result().empfehlung.titel }}</strong> {{ result().empfehlung.text }}
        Gerechnet mit 48 % Marge (eigene Erzeugnisse); für zugekaufte Ware rechnet der
        Amortisationsrechner mit 30 %.
      </p>
      <p class="disclaimer" i18n="@@standort.disclaimer.modell">
        Kosten, Marge und Break-even stammen aus demselben Modell wie unser Amortisationsrechner
        (Datenbasis: erste 450 Automaten, Ø 2.080 €/Monat). Die Gewichtung der Standortfaktoren
        verfeinern wir laufend mit den Bestandsdaten – die Einschätzung wird dadurch genauer, das
        Prinzip bleibt gleich.
      </p>
    </div>
  </div>
</div>
